/**
 * A task's priority — Aaron, Oct 25, note 1.5 ("like in Microsoft Planner,
 * each task could have a priority drop down"); `docs/activity.md` §8.7.
 *
 * Planner's four and Planner's looks: **Urgent** a bell and **Important** an
 * exclamation mark, both red-family; **Low** a down arrow; **Medium** the
 * default, which draws nothing on a card or a row, so the common case stays
 * quiet and only a priority somebody chose catches the eye.
 *
 * On a card, a row and a phone card it is **the icon in a chip of its own
 * colour, and no word** (Aaron, Oct 26, answer 4: "just show icons in colored
 * chips for priority to save space. no text") — the word is the chip's hover
 * and what a screen reader says. The sheet's dropdown keeps its words.
 */
import { TASK_PRIORITIES, TASK_PRIORITY_LABELS, type TaskPriority } from '@rowted/shared'
import { MenuSelect } from '../../components/menu-select.js'
import { cn } from '../../lib/cn.js'
import { Glyph, type GlyphName } from '../glyphs.js'

const LOOK: Record<TaskPriority, { glyph: GlyphName; ink: string; chip: string }> = {
  urgent: { glyph: 'bell', ink: 'text-danger', chip: 'bg-danger-bg' },
  important: { glyph: 'exclaim', ink: 'text-warn', chip: 'bg-warn-bg' },
  medium: { glyph: 'dot', ink: 'text-text-faint', chip: 'bg-surface-alt' },
  low: { glyph: 'arrow_down', ink: 'text-info', chip: 'bg-info-bg' },
}

/** The priority's glyph in its colour. */
export function PriorityGlyph({ priority, size = 14 }: { priority: TaskPriority; size?: number }) {
  return (
    <span className={cn('inline-flex shrink-0', LOOK[priority].ink)}>
      <Glyph name={LOOK[priority].glyph} size={size} stroke={priority === 'important' ? 2.6 : 2} />
    </span>
  )
}

/**
 * On a card, a row or a phone card: the glyph in a square chip of its colour
 * — 22 px beside a card's due chip, 20 px on a row's title line and among a
 * phone card's facts — named for a hover and a screen reader (*Urgent
 * priority*); for Medium, the default, nothing at all. `words` is the
 * Priority column's cell, which is about the priority and so says it.
 */
export function PriorityMark({
  priority, size = 'card', words = false,
}: { priority: TaskPriority; size?: 'card' | 'row'; words?: boolean }) {
  const label = TASK_PRIORITY_LABELS[priority]
  if (priority === 'medium') return words ? <span className="text-[13px] text-text-muted">{label}</span> : null
  /* Beside its word the chip is only a look; alone it is named. */
  const named = words ? { 'aria-hidden': true } : { title: label, role: 'img', 'aria-label': `${label} priority` }
  const chip = (
    <span data-priority={words ? undefined : priority} {...named}
      className={cn('inline-flex shrink-0 items-center justify-center', LOOK[priority].chip, LOOK[priority].ink,
        size === 'card' ? 'h-[22px] w-[22px] rounded-[6px]' : 'h-5 w-5 rounded-[5px]')}>
      <Glyph name={LOOK[priority].glyph} size={size === 'card' ? 12 : 11} stroke={priority === 'important' ? 2.8 : 2.2} />
    </span>
  )
  if (!words) return chip
  return (
    <span data-priority={priority} className="inline-flex items-center gap-1.5 text-[13px] text-text">
      {chip}
      {label}
    </span>
  )
}

/** The Task sheet's dropdown: the four, most pressing first. */
export function PriorityField({
  value, onChange, disabled,
}: { value: TaskPriority; onChange: (priority: TaskPriority) => void; disabled?: boolean }) {
  return (
    <MenuSelect
      label="Priority" size="field" noSearch value={value} disabled={disabled}
      options={TASK_PRIORITIES.map((priority) => ({
        value: priority, label: TASK_PRIORITY_LABELS[priority], glyph: <PriorityGlyph priority={priority} />,
      }))}
      onChange={(next) => { if (next && next !== value) onChange(next as TaskPriority) }}
      className="w-full"
    />
  )
}
